import {
  Input,
  Row,
  Space
} from 'antd';
import React, { memo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';

import Alert from '@fmbt/ui/Alert';
import Button from '@fmbt/ui/Button';
import Checkbox from '@fmbt/ui/Checkbox';
import { HStack } from '@fmbt/ui/layout';
import UserRoleTag from '@fmbt/ui/UserRoleTag';
import InternalAdminNote, { InternalAdminMessage } from 'components/atoms/InternalAdminNote';
import DefaultFormItem from 'components/composites/DefaultFormItem';
import FieldsValue from 'components/forms/FieldsValue';
import FieldValue from 'components/forms/FieldValue';
import FormItemCol from 'components/forms/FormItemCol';
import FormItemInputPhoneNumber from 'components/forms/FormItemInputPhoneNumber';
import { EM_DASH } from 'constants/index';
import { InternalAdminRole } from 'constants/internal-admin-roles';
import useTranslation from 'hooks/useTranslation';
import { useEnterpriseId } from 'store/modules/enterprise/hooks';
import clearErrors from 'store/modules/error/actions';
import { makeSelectFirstErrorMessage } from 'store/modules/error/selectors';
import { makeSelectIsLoading } from 'store/modules/loading/selectors';
import MyUserActionType from 'store/modules/myUser/constants';
import { useIsEnterpriseAdmin, useMyUserHasInternalPermission } from 'store/modules/myUser/hooks';
import { makeSelectIsMyUser } from 'store/modules/myUser/selectors';
import UserActionType from 'store/modules/users/constants';
import { MAX_WIDTH_MOBILE } from 'style/constants';
import { ProtectedResource, ResourceAction } from 'types/action.enum';
import Membership from 'types/models/membership';
import { UserId } from 'types/models/user';

import { UserRemoveButton } from './components/UserRemoveButton';
import FormItemEmail from '../FormItemEmail';
import FormItemRole from '../FormItemRole';

const actions = [
  UserActionType.ADD_USER_REQUEST,
  UserActionType.EDIT_USER_REQUEST,
  MyUserActionType.EDIT_MY_USER_REQUEST
];

const selectErrorMessage = makeSelectFirstErrorMessage(actions);
const selectIsLoading = makeSelectIsLoading(actions);

interface Props {
  namePrefix?: string;
  userId?: UserId;
  readOnly: boolean;
  showEmailField?: boolean;
  showRoleField?: boolean;
  showSaveButton?: boolean;
  showUsernameField?: boolean;
  isFullSpanOnMobileHalfOnDesktop?: boolean;
  afterUserRemoved?: () => void;
  required?: boolean;
  firstNameAutoComplete?: string;
  lastNameAutoComplete?: string;
}

function UserFormItems({
  userId,
  readOnly,
  showSaveButton: showSaveButtonProp,
  showEmailField = true,
  showRoleField = true,
  showUsernameField = true,
  isFullSpanOnMobileHalfOnDesktop,
  afterUserRemoved,
  required = true,
  firstNameAutoComplete,
  lastNameAutoComplete
}: Props): JSX.Element | null {
  const dispatch = useDispatch();
  const errorMessage = useSelector(selectErrorMessage);
  const isLoading = useSelector(selectIsLoading);

  const { t } = useTranslation();

  const isEnterpriseAdmin = useIsEnterpriseAdmin();
  const hasInternalPermissionToRemoveMembership = useMyUserHasInternalPermission({
    resource: ProtectedResource.USER_MEMBERSHIP,
    action: ResourceAction.DELETE
  });
  const hasRemoveMembershipPermission = isEnterpriseAdmin || hasInternalPermissionToRemoveMembership;

  const currentEnterpriseId = useEnterpriseId();
  const isCurrentUser = useSelector(makeSelectIsMyUser(userId || 0));

  const mobileNumberLabel = t('MOBILE_NUMBER');

  useEffect(() => {
    dispatch(clearErrors(actions));
  }, [dispatch]);

  const showSaveButton = showSaveButtonProp ?? !readOnly;
  /**
   * Who can edit roles?
   *
   * |               |admin|non-admin|
   * |---------------|-----|---------|
   * |can edit self  |  ❌  |   ❌    |
   * |can edit others|  ✅  |   ❌    |
   *
   * This matrix (better to view in source code) applies no matter in Team
   * Member editing page or Profile Settings.
   */
  const allowEditRole = !readOnly && !isCurrentUser;
  /**
   * Who can remove membership?
   *
   * |                 |admin|non-admin|
   * |-----------------|-----|---------|
   * |can remove self  | ❌  |   ❌    |
   * |can remove others| ✅  |   ❌    |
   *
   * This matrix applies no matter in Team Member editing page or Profile Settings.
   */
  const showRemoveButton = !!userId && hasRemoveMembershipPermission && !isCurrentUser;

  const isCompact = useMediaQuery({ maxWidth: MAX_WIDTH_MOBILE });
  const rightButtonTakesAllSpace = showSaveButton && !showRemoveButton && isCompact;

  return (
    <>
      <Row gutter={24}>
        <FormItemCol
          isFullSpanOnMobileHalfOnDesktop={isFullSpanOnMobileHalfOnDesktop}
        >
          <DefaultFormItem
            name="firstName"
            label="First Name"
            rules={[
              {
                required,
                message: 'Please enter a first name'
              },
              {
                pattern: /^[A-Za-z ,.'-]+$/,
                message: 'First Name should not contain numbers'
              }
            ]}
            readOnly={readOnly}
          >
            <Input autoComplete={firstNameAutoComplete} placeholder="First Name" />
          </DefaultFormItem>
        </FormItemCol>

        <FormItemCol
          isFullSpanOnMobileHalfOnDesktop={isFullSpanOnMobileHalfOnDesktop}
        >
          <DefaultFormItem
            name="lastName"
            label="Last Name"
            rules={[
              {
                required,
                message: 'Please enter a last name'
              },
              {
                pattern: /^[A-Za-z ,.'-]+$/,
                message: 'Last Name should not contain numbers'
              }
            ]}
            readOnly={readOnly}
          >
            <Input autoComplete={lastNameAutoComplete} placeholder="Last Name" />
          </DefaultFormItem>
        </FormItemCol>

        {showUsernameField && (
          <FormItemCol
            isFullSpanOnMobileHalfOnDesktop={isFullSpanOnMobileHalfOnDesktop}
          >
            <DefaultFormItem
              name="username"
              label="Username"
              rules={[
                {
                  required,
                  message: 'Please enter a username'
                },
                {
                  pattern: new RegExp(
                    '^[a-zA-Z0-9](_(?!(\\.|_))|\\.(?!(_|\\.))|[a-zA-Z0-9#]){4,28}[a-zA-Z0-9]$'
                  ),
                  message: 'Please enter a valid username'
                }
              ]}
              readOnly={readOnly}
            >
              <Input placeholder="Username" />
            </DefaultFormItem>
          </FormItemCol>
        )}
        {showEmailField && (
          <FormItemCol
            isFullSpanOnMobileHalfOnDesktop={isFullSpanOnMobileHalfOnDesktop}
          >
            <FormItemEmail readOnly={readOnly} required={required} />
          </FormItemCol>
        )}
        {/* Mobile Number */}
        <FormItemCol
          isFullSpanOnMobileHalfOnDesktop={isFullSpanOnMobileHalfOnDesktop}
        >
          {readOnly ? (
            <FieldValue name="fullName">
              {(userFullName = 'This user') => (
                <DefaultFormItem
                  readOnly
                  name="mobileNumberFormatted"
                  label={mobileNumberLabel}
                  formatReadOnlyValue={(value) => {
                    if (!value) {
                      return (
                        <div className="body-muted-copy">
                          {userFullName} hasn&apos;t provided a {mobileNumberLabel.toLowerCase()} yet.
                        </div>
                      );
                    }
                    return <div className="body-copy">{value}</div>;
                  }}
                />
              )}
            </FieldValue>
          ) : (
            <FieldValue name="enableSmsNotifications">
              {(smsNotificationsEnabled = false) => (
                <FormItemInputPhoneNumber
                  name="mobileNumber"
                  label={mobileNumberLabel}
                  rules={[
                    {
                      required: !!smsNotificationsEnabled && required,
                      message: `Please enter a ${mobileNumberLabel.toLowerCase()}`
                    }
                  ]}
                  prefixName="mobileNumberCountryCode"
                />
              )}
            </FieldValue>
          )}
        </FormItemCol>
        <FormItemCol
          isFullSpanOnMobileHalfOnDesktop={isFullSpanOnMobileHalfOnDesktop}
        >
          {readOnly ? (
            <FieldValue name="fullName">
              {(userFullName = 'This user') => (
                <DefaultFormItem
                  readOnly
                  name="landlineNumberFormatted"
                  label="Landline Number"
                  formatReadOnlyValue={(value) => {
                    if (!value) {
                      return (
                        <div className="body-muted-copy">
                          {userFullName} hasn&apos;t provided a landline number yet.
                        </div>
                      );
                    }
                    return <div className="body-copy">{value}</div>;
                  }}
                />
              )}
            </FieldValue>
          ) : (
            <FormItemInputPhoneNumber
              name="landlineNumber"
              label="Landline Number"
              rules={[
                {
                  required: false,
                  message: 'Please enter a landline number'
                }
              ]}
              prefixName="landlineNumberCountryCode"
            />
          )}
        </FormItemCol>
        {showRoleField && (
          <FormItemCol
            isFullSpanOnMobileHalfOnDesktop={isFullSpanOnMobileHalfOnDesktop}
          >
            {allowEditRole ? (
              <FormItemRole rules={[{ required, message: 'Please select a role' }]} />
            ) : (
              <DefaultFormItem label="Role" required>
                <FieldsValue names={['role', 'adminRole']}>
                  {({ role, adminRole }) => {
                    // non-admin
                    if (!adminRole || adminRole === InternalAdminRole._NONE) {
                      return role ? <UserRoleTag role={role} /> : <>{EM_DASH}</>;
                    }
                    // internal admin or both roles
                    return (
                      <>
                        {[role, adminRole].filter(Boolean).map(r => (
                          <UserRoleTag key={r} role={r} />
                        ))}
                      </>
                    );
                  }}
                </FieldsValue>
              </DefaultFormItem>
            )}
          </FormItemCol>
        )}
      </Row>
      {!readOnly && (
        <FieldValue name="memberships">
          {(memberships: Membership[] = []) => {
            const isCreatingNewUser = !userId;
            const isEditingEnterpriseMember = memberships.some(m => m.enterpriseId === currentEnterpriseId);
            const allowSetNotificationPreferences = isCreatingNewUser || isEditingEnterpriseMember;
            return (
              <Row>
                <FormItemCol
                  isFullSpanOnMobileHalfOnDesktop={isFullSpanOnMobileHalfOnDesktop}
                >
                  <Space direction="vertical">
                    <div className="body-emphasis-copy">Notifications Preference</div>
                    <InternalAdminNote message={InternalAdminMessage.SUBSCRIBE_TO_ALERTS} />
                    <div className="body-copy">Send alert notifications via</div>
                    <DefaultFormItem
                      name="enableEmailNotifications"
                      valuePropName="checked"
                      noStyle
                    >
                      <Checkbox disabled={!allowSetNotificationPreferences}>Email</Checkbox>
                    </DefaultFormItem>
                    <DefaultFormItem
                      name="enableSmsNotifications"
                      valuePropName="checked"
                    >
                      <Checkbox disabled={!allowSetNotificationPreferences}>{t('SMS')}</Checkbox>
                    </DefaultFormItem>
                  </Space>
                </FormItemCol>
              </Row>
            );
          }}
        </FieldValue>
      )}
      {errorMessage && (
        <DefaultFormItem>
          <Alert message={errorMessage} />
        </DefaultFormItem>
      )}
      {(showRemoveButton || showSaveButton) && (
        <HStack
          block
          gap={16}
          justify={showRemoveButton ? 'space-between' : 'end'}
        >
          {showRemoveButton && (
            <FieldValue name="fullName">
              {(userFullName = 'this user') => (
                <UserRemoveButton
                  userId={userId}
                  userFullName={userFullName}
                  isFormSaving={isLoading}
                  afterUserRemoved={afterUserRemoved}
                />
              )}
            </FieldValue>
          )}
          {showSaveButton && (
            <Button
              htmlType="submit"
              loading={isLoading}
              type="primary"
              width={rightButtonTakesAllSpace ? 'block' : 96}
            >
              SAVE
            </Button>
          )}
        </HStack>
      )}
    </>
  );
}

export default memo(UserFormItems);
